import React from "react";
import { Col, Pagination, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import usePagination from "hooks/usePagination";
import {
  faAngleRight,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import Button from "components/base/Button";
import { useAdvanceTableContext } from "providers/AdvanceTableProvider";

interface AdvanceTableFooterProps {
  className?: string;
  pagination?: boolean;
  navBtn?: boolean;
  showViewAllBtn?: boolean;
  handleNextPage: () => void;
  handlePreviousPage: () => void;
  totalPages: number;
  totalRecords: number;
  recordsPerPage: number;
  CanPreviousPage: () => boolean;
  CanNextPage: () => boolean;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  isAllVisibleData?: boolean;
  setIsAllVisibleData?: (value: boolean) => void;
}

const CustomAdvanceTableFooter: React.FC<AdvanceTableFooterProps> = ({
  className,
  pagination,
  handleNextPage,
  handlePreviousPage,
  totalRecords,
  recordsPerPage,
  CanPreviousPage,
  currentPage,
  CanNextPage,
  setCurrentPage,
  isAllVisibleData,
  setIsAllVisibleData,
  showViewAllBtn = true,
}) => {
  const { setPageSize } = useAdvanceTableContext();

  const getPageCount = () => {
    return Math.ceil(totalRecords / recordsPerPage);
  };

  const { hasNextEllipsis, hasPrevEllipsis, visiblePaginationItems } =
    usePagination({
      currentPageNo: currentPage,
      totalPage: getPageCount(),
      maxPaginationButtonCount: 5,
    });

  let endsWith;
  if (recordsPerPage * currentPage < totalRecords) {
    endsWith = recordsPerPage * currentPage;
  } else {
    endsWith = totalRecords;
  }

  return (
    <Row className={classNames(className, "align-items-center py-1")}>
      <Col className="d-flex fs-9">
        <p className="mb-0 d-none d-sm-block me-3 fw-semibold text-body">
          {isAllVisibleData ? 1 : recordsPerPage * (currentPage - 1) + 1} to{" "}
          {isAllVisibleData ? totalRecords : endsWith}
          <span className="text-body-tertiary"> items of </span>
          {totalRecords}
        </p>
        {showViewAllBtn && (
          <Button
            variant="link"
            className="p-0 fw-semibold"
            endIcon={
              <FontAwesomeIcon icon={faAngleRight} className="ms-1 fs-9" />
            }
            onClick={() => {
              if (setIsAllVisibleData) {
                setIsAllVisibleData(!isAllVisibleData);
                setPageSize(isAllVisibleData ? recordsPerPage : totalRecords);
                if (isAllVisibleData) {
                  setCurrentPage(1);
                }
              }
            }}
          >
            View {isAllVisibleData ? "less" : "all"}
          </Button>
        )}
      </Col>

      {pagination && (
        <Col xs="auto">
          <Pagination className="mb-0 justify-content-center">
            {!isAllVisibleData ? (
              <>
                <Pagination.Prev
                  disabled={!CanPreviousPage()}
                  onClick={handlePreviousPage}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </Pagination.Prev>
              </>
            ) : (
              <>
                <Pagination.Prev disabled>
                  <FontAwesomeIcon icon={faChevronLeft} />
                </Pagination.Prev>
              </>
            )}

            {hasPrevEllipsis && (
              <>
                <Pagination.Item
                  active={isAllVisibleData ? true : currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                >
                  1
                </Pagination.Item>
                <Pagination.Ellipsis disabled />
              </>
            )}

            <>
              {!isAllVisibleData ? (
                visiblePaginationItems.map((page) => (
                  <Pagination.Item
                    key={page}
                    active={currentPage === page}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Pagination.Item>
                ))
              ) : (
                <>
                  <Pagination.Item
                    active={true}
                    onClick={() => setCurrentPage(1)}
                  >
                    1
                  </Pagination.Item>
                </>
              )}
            </>

            {hasNextEllipsis && (
              <>
                <Pagination.Ellipsis disabled />
                <Pagination.Item
                  active={
                    isAllVisibleData ? true : currentPage === getPageCount()
                  }
                  onClick={() => setCurrentPage(getPageCount())}
                >
                  {getPageCount()}
                </Pagination.Item>
              </>
            )}
            {!isAllVisibleData ? (
              <>
                <Pagination.Next
                  disabled={!CanNextPage()}
                  onClick={handleNextPage}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </Pagination.Next>
              </>
            ) : (
              <>
                <Pagination.Next disabled>
                  <FontAwesomeIcon icon={faChevronRight} />
                </Pagination.Next>
              </>
            )}
          </Pagination>
        </Col>
      )}
    </Row>
  );
};

export default CustomAdvanceTableFooter;
